import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Lock, CheckCircle, Eye, EyeOff, XCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordWithToken = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Color Palette (matching Login page)
  const colors = {
    primaryBlue: '#0082B0',
    primaryLight: '#0EA5E9',
    primaryDark: '#0284C7',
    background: '#0A1520',
    cardBg: '#0F1D2A',
    inputBg: '#0F172A',
    border: '#1E3A5F',
    text: '#E2E8F0',
    mutedText: '#94A3B8',
  };

  useEffect(() => {
    if (!token) {
      setError("Missing reset token. Please request a new password reset link.");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast.error("Please enter a new password");
      return;
    }

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          token,
          new_password: password 
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        toast.success("Password reset successful!");
      } else {
        toast.error(data.detail || "Failed to reset password");
        if (data.detail?.includes("expired")) {
          setError("This reset link has expired. Please request a new one.");
        }
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Error state
  if (error) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ 
          background: `linear-gradient(135deg, ${colors.background} 0%, #0D2137 50%, ${colors.background} 100%)`,
          fontFamily: "'DM Sans', sans-serif"
        }}
      >
        <div 
          className="w-full max-w-md p-8 rounded-2xl shadow-2xl text-center"
          style={{ 
            backgroundColor: colors.cardBg,
            border: `1px solid ${colors.border}`
          }}
        >
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
          >
            <XCircle size={32} className="text-red-500" />
          </div>
          
          <h1 
            className="text-2xl font-bold mb-4"
            style={{ color: colors.text }}
          >
            Invalid Reset Link
          </h1>
          
          <p 
            className="mb-8"
            style={{ color: colors.mutedText }}
          >
            {error}
          </p>
          
          <Link to="/forgot-password">
            <Button
              className="w-full h-12 text-white mb-3"
              style={{ 
                background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primaryDark} 100%)`
              }}
            >
              Request New Reset Link
            </Button>
          </Link>
          
          <Link 
            to="/login"
            className="text-sm transition-colors hover:opacity-80"
            style={{ color: colors.primaryLight }}
          >
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ 
          background: `linear-gradient(135deg, ${colors.background} 0%, #0D2137 50%, ${colors.background} 100%)`,
          fontFamily: "'DM Sans', sans-serif"
        }}
      >
        <div 
          className="w-full max-w-md p-8 rounded-2xl shadow-2xl text-center"
          style={{ 
            backgroundColor: colors.cardBg,
            border: `1px solid ${colors.border}`
          }}
        >
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}
          >
            <CheckCircle size={32} className="text-emerald-500" />
          </div>
          
          <h1 
            className="text-2xl font-bold mb-4"
            style={{ color: colors.text }}
          >
            Password Reset Complete
          </h1>
          
          <p 
            className="mb-8"
            style={{ color: colors.mutedText }}
          >
            Your password has been successfully reset. You can now log in with your new password.
          </p>
          
          <Button
            onClick={() => navigate("/login")}
            className="w-full h-12 text-white"
            style={{ 
              background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primaryDark} 100%)`
            }}
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ 
        background: `linear-gradient(135deg, ${colors.background} 0%, #0D2137 50%, ${colors.background} 100%)`,
        fontFamily: "'DM Sans', sans-serif"
      }}
    >
      <div 
        className="w-full max-w-md p-8 rounded-2xl shadow-2xl"
        style={{ 
          backgroundColor: colors.cardBg,
          border: `1px solid ${colors.border}`
        }}
      >
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 mb-6 text-sm transition-colors hover:opacity-80"
          style={{ color: colors.primaryLight }}
        >
          <ArrowLeft size={16} />
          Back to Login
        </Link>

        <div className="text-center mb-8">
          <div 
            className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: 'rgba(14, 165, 233, 0.1)' }}
          >
            <Lock size={28} style={{ color: colors.primaryLight }} />
          </div>
          
          <h1 
            className="text-2xl font-bold mb-2"
            style={{ color: colors.text }}
          >
            Set New Password
          </h1>
          
          <p style={{ color: colors.mutedText }}>
            Enter your new password below.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <label 
              className="text-xs font-medium uppercase tracking-wider"
              style={{ color: colors.mutedText }}
            >
              New Password
            </label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-14 px-4 pr-12 border-0 text-base"
                style={{ 
                  backgroundColor: colors.inputBg,
                  color: colors.text
                }}
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors hover:opacity-80"
                style={{ color: colors.mutedText }}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <p className="text-xs" style={{ color: colors.mutedText }}>
              Must be at least 8 characters
            </p>
          </div>

          <div className="space-y-2">
            <label 
              className="text-xs font-medium uppercase tracking-wider"
              style={{ color: colors.mutedText }}
            >
              Confirm Password
            </label>
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="h-14 px-4 border-0 text-base"
              style={{ 
                backgroundColor: colors.inputBg,
                color: colors.text
              }}
              data-testid="confirm-password-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-14 text-base font-medium text-white hover:opacity-90"
            style={{ 
              background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primaryDark} 100%)`
            }}
            disabled={loading}
            data-testid="reset-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Resetting...
              </>
            ) : (
              <>
                <Lock className="mr-2 h-5 w-5" />
                Reset Password
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordWithToken;
